import type { Brand, Category, Item } from '@erp/shared';
import { Check, Download, ExternalLink, ImageOff, Package, Sparkles, Upload, Wand2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, Combobox, type ComboboxOption, FormField, Skeleton } from '../../components/ui/index.js';
import { api } from '../../lib/api.js';

type GenType = 'TEXTTOIAMGE' | 'IMAGETOIAMGE';

/** Strip HTML + collapse whitespace so a rich-text item description reads as a plain prompt fragment. */
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/&[a-z]+;/gi, ' ').replace(/\s+/g, ' ').trim();
}

/** Curated "pro" photography styles the user can stack onto a prompt. */
const STYLE_PRESETS: Array<{ key: string; label: string; text: string }> = [
  { key: 'studio', label: 'Studio', text: 'studio product shot, seamless white background, soft-box lighting, sharp focus, high detail, commercial photography, 4k' },
  { key: 'ecommerce', label: 'E-commerce', text: 'pure white background, centered, even lighting, no harsh shadows, clean catalog photo' },
  { key: 'lifestyle', label: 'Lifestyle', text: 'lifestyle scene, natural daylight, shallow depth of field, cozy realistic environment, editorial style' },
  { key: 'hero', label: 'Hero banner', text: 'dramatic hero shot, cinematic lighting, bold composition, vibrant colors, marketing banner' },
  { key: 'flatlay', label: 'Flat lay', text: 'top-down flat lay, neatly arranged props, soft pastel background, gentle shadows' },
  { key: 'dark', label: 'Dark & moody', text: 'dark moody background, dramatic rim lighting, premium luxury feel, reflective surface' },
];
/** Appended by "Enhance" — generic quality boosters. */
const ENHANCE_SUFFIX = 'professional photography, photorealistic, sharp focus, balanced composition, realistic textures, high resolution, 8k';

interface GeneratedImage {
  url: string;
  attachmentId: string | null;
  downloadUrl: string | null;
}
interface GenerateResult {
  id: string;
  status: 'succeeded' | 'failed';
  images: GeneratedImage[];
  error: string | null;
}
interface HistoryItem {
  id: string;
  prompt: string;
  type: GenType;
  status: 'pending' | 'succeeded' | 'failed';
  imageUrl: string | null;
  error: string | null;
  createdAt: string;
}

/**
 * AI Image Studio — text→image (and image→image via reference URLs) through the
 * Nano Banana provider. The server polls the provider and persists the result to
 * Files; we display the provider's public image URL (an authed <img> to the
 * attachment wouldn't carry the bearer token).
 */
export function ImageStudioPage(): JSX.Element {
  const { t } = useTranslation('imagegen');
  const [prompt, setPrompt] = useState('');
  const [type, setType] = useState<GenType>('TEXTTOIAMGE');
  const [refUrls, setRefUrls] = useState('');
  // Image→image: reference images chosen from existing generations (their
  // public provider URLs work directly as the image to edit).
  const [selectedRefs, setSelectedRefs] = useState<string[]>([]);
  // Images the user uploaded from their device (turned into public URLs the
  // provider can fetch) — shown in the reference grid + auto-selected.
  const [uploaded, setUploaded] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<HistoryItem[] | null>(null);
  // Catalog, for the "from an item" prompt builder.
  const [items, setItems] = useState<Item[]>([]);
  const [catName, setCatName] = useState<Map<string, string>>(new Map());
  const [brandName, setBrandName] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    void Promise.allSettled([
      api().get<{ items: Item[] }>('/items'),
      api().get<{ items: Category[] }>('/categories'),
      api().get<{ items: Brand[] }>('/brands'),
    ]).then(([it, cat, br]) => {
      if (it.status === 'fulfilled') setItems(it.value.data.items.filter((x) => x.isActive));
      if (cat.status === 'fulfilled') setCatName(new Map(cat.value.data.items.map((c) => [c.id, c.name])));
      if (br.status === 'fulfilled') setBrandName(new Map(br.value.data.items.map((b) => [b.id, b.title])));
    });
  }, []);

  const itemOptions = useMemo<ComboboxOption[]>(
    () => items.map((it) => ({ value: it.id, label: `${it.code} — ${it.name}`, keywords: `${it.code} ${it.name}` })),
    [items],
  );

  /** Build a strong product-photo prompt from an item's catalogue data. */
  const buildItemPrompt = (it: Item): string => {
    const cat = it.categoryId ? catName.get(it.categoryId) : '';
    const br = it.brandId ? brandName.get(it.brandId) : '';
    const desc = stripHtml(it.description).slice(0, 220).replace(/[.\s]+$/, '');
    const head = [
      `Professional product photograph of ${it.name}`,
      br ? `by ${br}` : '',
      cat ? `(${cat})` : '',
    ].filter(Boolean).join(' ');
    return `${head}.${desc ? ` ${desc}.` : ''} ${STYLE_PRESETS[0]!.text}.`;
  };

  const onPickItem = (id: string | undefined): void => {
    const it = items.find((x) => x.id === id);
    if (it) setPrompt(buildItemPrompt(it));
  };

  // Existing images (current result + recent history) the user can feed back
  // into image→image. Provider URLs are public, so they work as references.
  const currentImages = useMemo(() => {
    const urls: string[] = [...uploaded];
    for (const im of result?.images ?? []) if (im.url) urls.push(im.url);
    for (const h of history ?? []) if (h.status === 'succeeded' && h.imageUrl) urls.push(h.imageUrl);
    return [...new Set(urls)];
  }, [uploaded, result, history]);

  const toggleRef = (url: string): void =>
    setSelectedRefs((prev) => (prev.includes(url) ? prev.filter((u) => u !== url) : [...prev, url]));

  // Upload a local image → a public URL the provider fetches server-side. The
  // uploaded image is added to the grid AND auto-selected as a base to edit,
  // and the mode flips to image→image so it's actually sent.
  const uploadSource = async (file: File): Promise<void> => {
    const allowed = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
    if (!allowed.includes(file.type)) {
      toast.error(t('studio.uploadType', { defaultValue: 'Use a PNG, JPEG, WebP or GIF image.' }));
      return;
    }
    setUploading(true);
    try {
      const body = await file.arrayBuffer();
      const r = await api().post<{ url: string }>('/imagegen/source-upload', body, {
        params: { mimeType: file.type },
        headers: { 'Content-Type': 'application/octet-stream' },
        transformRequest: [(d) => d],
      });
      const url = r.data.url;
      setUploaded((prev) => [url, ...prev.filter((u) => u !== url)]);
      setSelectedRefs((prev) => (prev.includes(url) ? prev : [...prev, url]));
      setType('IMAGETOIAMGE');
    } catch {
      toast.error(t('studio.uploadFailed', { defaultValue: 'Upload failed — try a smaller image (max 10 MB).' }));
    } finally {
      setUploading(false);
    }
  };

  const clearAll = (): void => {
    setPrompt('');
    setSelectedRefs([]);
    setUploaded([]);
    setRefUrls('');
    setResult(null);
    setError(null);
  };

  /** Append a fragment to the prompt unless it's already there. */
  const appendToPrompt = (fragment: string): void => {
    setPrompt((p) => {
      const base = p.trim();
      if (!base) return fragment;
      if (base.toLowerCase().includes(fragment.toLowerCase())) return base;
      return `${base.replace(/[.,\s]+$/, '')}, ${fragment}`;
    });
  };

  const loadHistory = (): void => {
    void api()
      .get<{ items: HistoryItem[] }>('/imagegen/generations', { params: { limit: 12 } })
      .then((r) => setHistory(r.data.items))
      .catch(() => setHistory([]));
  };
  useEffect(loadHistory, []);

  const onGenerate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!prompt.trim() || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const manual = refUrls.split(/\s+/).map((s) => s.trim()).filter(Boolean);
      const imageUrls = type === 'IMAGETOIAMGE'
        ? [...new Set([...selectedRefs, ...manual])]
        : undefined;
      const r = await api().post<GenerateResult>('/imagegen/generate', {
        prompt: prompt.trim(),
        type,
        ...(imageUrls && imageUrls.length > 0 ? { imageUrls } : {}),
      });
      setResult(r.data);
      if (r.data.status === 'failed') setError(r.data.error);
      loadHistory();
    } catch (err) {
      setError(String((err as Error).message ?? err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Sparkles className="h-6 w-6 text-brand" aria-hidden />
          {t('studio.title', { defaultValue: 'AI Image Studio' })}
        </h1>
        <p className="mt-1 text-sm text-fg-muted">
          {t('studio.subtitle', { defaultValue: 'Generate images from a text prompt with Nano Banana.' })}
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr]">
        <Card className="p-6">
          <form onSubmit={onGenerate} className="flex flex-col gap-4" noValidate>
            <FormField id="ig-item" label={t('studio.fromItem', { defaultValue: 'Start from an item' })}>
              <Combobox
                id="ig-item"
                options={itemOptions}
                onChange={onPickItem}
                clearOnSelect={false}
                placeholder={t('studio.fromItemPlaceholder', { defaultValue: 'Pick a product to build a prompt…' })}
                ariaLabel={t('studio.fromItem', { defaultValue: 'Start from an item' })}
              />
              <p className="mt-1 flex items-center gap-1 text-xs text-fg-muted">
                <Package className="h-3 w-3" aria-hidden />
                {t('studio.fromItemHint', { defaultValue: 'Fills the prompt with the product’s name, brand & details — then tweak it.' })}
              </p>
            </FormField>

            <FormField id="ig-prompt" label={t('studio.prompt', { defaultValue: 'Prompt' })} required>
              <textarea
                id="ig-prompt"
                rows={4}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder={t('studio.promptPlaceholder', {
                  defaultValue: 'A photorealistic product shot of a ceramic mug on a marble counter, soft daylight',
                })}
                className="w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm"
              />
              {/* Pro-prompt helpers: stack a style, or enhance with quality boosters. */}
              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                {STYLE_PRESETS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => appendToPrompt(s.text)}
                    className="rounded-full border border-border bg-bg-subtle px-2.5 py-1 text-xs font-medium text-fg-muted transition-colors hover:border-brand hover:text-brand"
                  >
                    + {t(`studio.style.${s.key}`, { defaultValue: s.label })}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => appendToPrompt(ENHANCE_SUFFIX)}
                  disabled={!prompt.trim()}
                  className="ms-auto inline-flex items-center gap-1 rounded-full border border-brand/40 bg-brand/10 px-2.5 py-1 text-xs font-semibold text-brand transition-colors hover:bg-brand/20 disabled:opacity-50"
                >
                  <Wand2 className="h-3.5 w-3.5" aria-hidden />
                  {t('studio.enhance', { defaultValue: 'Enhance' })}
                </button>
              </div>
            </FormField>

            <FormField id="ig-type" label={t('studio.type', { defaultValue: 'Mode' })}>
              <select
                id="ig-type"
                className="block h-10 w-full rounded-md border border-border bg-bg px-3 text-sm"
                value={type}
                onChange={(e) => setType(e.target.value as GenType)}
              >
                <option value="TEXTTOIAMGE">{t('studio.typeText', { defaultValue: 'Text → image' })}</option>
                <option value="IMAGETOIAMGE">{t('studio.typeImage', { defaultValue: 'Image → image (edit)' })}</option>
              </select>
            </FormField>

            {type === 'IMAGETOIAMGE' && (
              <FormField id="ig-refs" label={t('studio.reference', { defaultValue: 'Image(s) to edit' })}>
                {/* Upload your own image → it's sent to Nano Banana as the source to edit. */}
                <label className={`mb-2 inline-flex w-fit cursor-pointer items-center gap-1.5 rounded-md border border-brand/40 bg-brand/5 px-3 py-2 text-sm font-medium text-brand transition hover:bg-brand/10 ${uploading ? 'pointer-events-none opacity-60' : ''}`}>
                  <Upload className="h-4 w-4" aria-hidden />
                  {uploading ? t('studio.uploading', { defaultValue: 'Uploading…' }) : t('studio.upload', { defaultValue: 'Upload an image to edit' })}
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    className="hidden"
                    disabled={uploading}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void uploadSource(f); e.target.value = ''; }}
                  />
                </label>
                {currentImages.length > 0 ? (
                  <div className="grid grid-cols-4 gap-2 sm:grid-cols-5">
                    {currentImages.map((url) => {
                      const sel = selectedRefs.includes(url);
                      return (
                        <button
                          key={url}
                          type="button"
                          onClick={() => toggleRef(url)}
                          aria-pressed={sel}
                          className={`relative aspect-square overflow-hidden rounded-md border-2 transition ${sel ? 'border-brand ring-2 ring-brand/30' : 'border-border hover:border-brand/50'}`}
                        >
                          <img src={url} alt="" className="h-full w-full object-cover" />
                          {sel && (
                            <span className="absolute end-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-brand text-brand-contrast">
                              <Check className="h-3 w-3" aria-hidden />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-fg-muted">
                    {t('studio.referenceNone', { defaultValue: 'Upload an image above (or generate one first), then pick it here to edit.' })}
                  </p>
                )}
                <p className="mt-1.5 text-xs text-fg-muted">
                  {selectedRefs.length > 0
                    ? t('studio.referenceSelected', { defaultValue: '{{n}} image(s) selected to edit.', n: selectedRefs.length })
                    : t('studio.referencePick', { defaultValue: 'Tap an image above to use it as the base to edit.' })}
                </p>
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-fg-muted hover:text-fg">
                    {t('studio.referenceUrlsToggle', { defaultValue: 'or paste image URLs' })}
                  </summary>
                  <textarea
                    id="ig-refs"
                    rows={2}
                    value={refUrls}
                    onChange={(e) => setRefUrls(e.target.value)}
                    placeholder="https://…/photo.png"
                    className="mt-2 w-full resize-y rounded-md border border-border bg-bg px-3 py-2 text-sm"
                  />
                  <p className="mt-1 text-xs text-fg-muted">
                    {t('studio.referenceHint', { defaultValue: 'One URL per line — must be publicly reachable.' })}
                  </p>
                </details>
              </FormField>
            )}

            {error && (
              <p className="rounded-md border border-danger/30 bg-danger/5 px-3 py-2 text-sm text-danger" role="alert">
                {error}
              </p>
            )}

            <div className="flex items-center gap-2">
              <Button type="submit" loading={busy} disabled={!prompt.trim()}>
                <Sparkles className="h-4 w-4" />
                {busy
                  ? t('studio.generating', { defaultValue: 'Generating…' })
                  : t('studio.generate', { defaultValue: 'Generate' })}
              </Button>
              <Button
                type="button"
                variant="secondary"
                onClick={clearAll}
                disabled={busy || (!prompt.trim() && selectedRefs.length === 0 && !refUrls.trim() && !result)}
              >
                {t('studio.clear', { defaultValue: 'Clear' })}
              </Button>
            </div>
          </form>
        </Card>

        <Card className="flex min-h-[20rem] items-center justify-center p-6">
          {busy ? (
            <div className="flex flex-col items-center gap-3 text-fg-muted">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-border border-t-brand" aria-hidden />
              <p className="text-sm">{t('studio.generating', { defaultValue: 'Generating… this can take ~10–30s' })}</p>
            </div>
          ) : result?.status === 'succeeded' && result.images.length > 0 ? (
            <div className="grid w-full gap-3">
              {result.images.map((img, i) => (
                <figure key={i} className="overflow-hidden rounded-lg border border-border">
                  <img src={img.url} alt={prompt} className="w-full object-contain" />
                  <figcaption className="flex items-center justify-end gap-3 border-t border-border bg-bg-subtle px-3 py-2 text-xs">
                    <a href={img.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-brand hover:underline">
                      <ExternalLink className="h-3.5 w-3.5" />
                      {t('studio.openOriginal', { defaultValue: 'Open original' })}
                    </a>
                    <a href={img.url} download className="inline-flex items-center gap-1 text-fg-muted hover:text-fg">
                      <Download className="h-3.5 w-3.5" />
                      {t('studio.download', { defaultValue: 'Download' })}
                    </a>
                  </figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-center text-fg-subtle">
              <ImageOff className="h-10 w-10" aria-hidden />
              <p className="text-sm">{t('studio.resultPlaceholder', { defaultValue: 'Your generated image will appear here.' })}</p>
            </div>
          )}
        </Card>
      </div>

      <Card className="p-6">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-fg-subtle">
          {t('studio.history', { defaultValue: 'Recent generations' })}
        </h2>
        {history === null ? (
          <Skeleton className="h-24 w-full" />
        ) : history.length === 0 ? (
          <p className="text-sm text-fg-muted">{t('studio.historyEmpty', { defaultValue: 'No generations yet.' })}</p>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
            {history.map((h) => (
              <div key={h.id} className="overflow-hidden rounded-lg border border-border" title={h.prompt}>
                {h.imageUrl && h.status === 'succeeded' ? (
                  <img src={h.imageUrl} alt={h.prompt} className="aspect-square w-full object-cover" />
                ) : (
                  <div className="grid aspect-square w-full place-items-center bg-bg-subtle text-[0.65rem] text-fg-subtle">
                    {h.status === 'failed'
                      ? t('studio.failed', { defaultValue: 'Failed' })
                      : '…'}
                  </div>
                )}
                <div className="truncate px-2 py-1 text-[0.7rem] text-fg-muted">{h.prompt}</div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
